/** Phase 14 Wave 3 — Module E — Admin Maintenance Tickets page. */
import { useCallback, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import AdminDataTable from "@/components/admin/AdminDataTable";
import { Wrench } from "lucide-react";

const STATUS_OPTIONS = ["open", "in_progress", "resolved", "closed"];
const PRIORITY_BADGES = {
  low: "bg-slate-100 text-slate-600",
  medium: "bg-amber-50 text-amber-700",
  high: "bg-orange-50 text-orange-700",
  urgent: "bg-red-50 text-red-700",
};
const STATUS_BADGES = {
  open: "bg-blue-50 text-blue-700",
  in_progress: "bg-indigo-50 text-indigo-700",
  resolved: "bg-emerald-50 text-emerald-700",
  closed: "bg-slate-100 text-slate-500",
};

export default function AdminMaintenance() {
  const [refresh, setRefresh] = useState(0);
  const fetchPage = useCallback(async ({ skip, limit, q }) => {
    const page = Math.floor(skip / limit);
    const { data } = await api.get("/admin/tickets", { params: { page, limit, q } });
    return data;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refresh]);
  const cycle = async (row, next) => {
    try {
      await api.patch(`/admin/tickets/${row.id}`, { status: next });
      toast.success(`Status → ${next}`);
      setRefresh((n) => n + 1);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Update failed");
    }
  };
  const columns = [
    { key: "title", label: "Ticket", render: (r) => (
      <div>
        <div className="flex items-center gap-1.5 font-medium text-slate-900">
          <Wrench className="h-3 w-3 text-slate-400" /> {r.title || "Untitled"}
        </div>
        <div className="font-mono text-[10px] text-slate-400">{r.id?.slice(0, 8)}</div>
      </div>
    )},
    { key: "priority", label: "Priority", render: (r) => (
      <span className={`inline-flex rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase ${PRIORITY_BADGES[r.priority] || "bg-slate-100"}`}>{r.priority}</span>
    )},
    { key: "status", label: "Status", render: (r) => (
      <span className={`inline-flex rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase ${STATUS_BADGES[r.status] || "bg-slate-100"}`}>{r.status?.replace("_", " ")}</span>
    )},
    { key: "reported_at", label: "Reported", render: (r) => (r.reported_at || "").slice(0, 10) || "—" },
  ];
  return (
    <div className="space-y-6">
      <div>
        <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-indigo-600">Module E</p>
        <h1 className="mt-1 text-2xl font-bold tracking-tight text-slate-900">Maintenance tickets</h1>
        <p className="mt-1 text-sm text-slate-500">All tickets across all landlords. Click an action to advance status.</p>
      </div>
      <AdminDataTable
        testid="admin-maintenance"
        columns={columns}
        fetchPage={fetchPage}
        searchPlaceholder="Search by title…"
        rowActions={(row) => {
          const idx = STATUS_OPTIONS.indexOf(row.status);
          const next = idx >= 0 && idx < STATUS_OPTIONS.length - 1 ? STATUS_OPTIONS[idx + 1] : null;
          return next ? [{ label: `→ ${next.replace("_", " ")}`, variant: "primary", onClick: (r) => cycle(r, next) }] : [];
        }}
      />
    </div>
  );
}
